import React, { useEffect, useState } from "react";
import { Button, Stack, styled, Typography } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getEvents,
  addEvent,
  editEvent,
  removeEvent,
  activateEvent,
  uploadEventHeader,
  closeEvent,
} from "../../api/event.api.js";
import CreateEditEventModal from "../../components/modals/createEditEvent.modal.js";
import moment from "moment";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useSnackbar } from "../../context/snackbar.context.js";
import { createRanking } from "../../api/ranking.api.js";

const AdminEventsPage = () => {
  const [openModal, setOpenModal] = useState(false);
  const [events, setEvents] = useState([]);
  const [refreshEvents, setRefreshEvents] = useState(false);
  const [eventToEdit, setEventToEdit] = useState();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    getEvents().then((response) => {
      setEvents(response.data);
    });
  }, [refreshEvents]);

  const handleSubmit = (formData) => {
    if (!eventToEdit) {
      addEvent(formData)
        .then((response) => {
          setRefreshEvents((prev) => !prev);
        })
        .catch((error) => {
          showSnackbar("Unable to add Event!", "error");
        });
    } else {
      editEvent(formData)
        .then((response) => {
          setRefreshEvents((prev) => !prev);
        })
        .catch((error) => {
          showSnackbar("Unable to edit Event!", "error");
        });
    }
  };

  const handleRemove = (id) => {
    removeEvent(id)
      .then(() => {
        setRefreshEvents((prev) => !prev);
      })
      .catch(() => {
        showSnackbar(
          "Unable to remove Event - It´s active or have tournaments!",
          "error"
        );
      });
  };

  const handleClose = (id) => {
    closeEvent(id)
      .then(() => {
        setRefreshEvents((prev) => !prev);
      })
      .catch(() => {
        showSnackbar(
          "Unable to close Event - It has open tournaments!",
          "error"
        );
      });
  };

  const handleActivate = (id) => {
    activateEvent(id)
      .then((response) => {
        setRefreshEvents((prev) => !prev);
      })
      .catch((error) => {});
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEventToEdit();
  };

  const handleEdit = (event) => {
    setEventToEdit(event);
    setOpenModal(true);
  };

  const handleUpload = (id, file) => {
    uploadEventHeader(id, file)
      .then((response) => {
        if (response.data) {
          showSnackbar("Header uploaded!", "success");
        } else {
          showSnackbar("Could not upload Header!", "error");
        }
      })
      .catch(() => {
        showSnackbar("Could not upload Header!", "error");
      });
  };

  const handleAddRanking = (id) => {
    createRanking(id)
      .then(() => {
        showSnackbar("Ranking created!", "success");
      })
      .catch(() => {
        showSnackbar("Ranking could not be created!", "error");
      });
  };

  const isEventActivable = (id) => {
    if (!events) {
      return false;
    }
    if (events.find((e) => e.isActive)) {
      return false;
    } else {
      return !events.find((e) => e.id === id).isActive;
    }
  };

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  return (
    <div>
      <Typography variant="h3" gutterBottom>
        Events Administration
      </Typography>
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Event Name</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Participants</TableCell>
              <TableCell>Header</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((row) => (
              <TableRow
                key={row.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {row.name}
                </TableCell>
                <TableCell>
                  {moment(row.start).format("DD.MM.YYYY HH:mm:ss")}
                </TableCell>
                <TableCell>
                  {moment(row.end).format("DD.MM.YYYY HH:mm:ss")}
                </TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.isActive ? "LIVE" : "CLOSED"}</TableCell>
                <TableCell>{row.participants.length}</TableCell>
                <TableCell>{row.imageName ? "Yes" : "No"}</TableCell>
                <TableCell>{row.location ? "Yes" : "No"}</TableCell>
                <TableCell>
                  <Stack spacing={1} direction="row">
                    <Button
                      variant="outlined"
                      onClick={() => handleAddRanking(row.id)}
                    >
                      Add Ranking
                    </Button>
                    <Button
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      startIcon={<FileUploadIcon />}
                    >
                      Upl. Header
                      <VisuallyHiddenInput
                        type="file"
                        onChange={(event) =>
                          handleUpload(row.id, event.target.files[0])
                        }
                      />
                    </Button>
                    <Button variant="outlined" onClick={() => handleEdit(row)}>
                      Edit
                    </Button>
                    {isEventActivable(row.id) && (
                      <Button
                        variant="outlined"
                        onClick={() => handleActivate(row.id)}
                      >
                        Activate
                      </Button>
                    )}

                    {row.isActive && (
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleClose(row.id)}
                      >
                        Close
                      </Button>
                    )}
                    {!row.isActive && (
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleRemove(row.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant="contained" onClick={handleOpenModal} sx={{ mt: 1 }}>
        Create Event
      </Button>

      {openModal && (
        <CreateEditEventModal
          aria-modal
          open={openModal}
          handleClose={handleCloseModal}
          handleSubmit={handleSubmit}
          eventData={eventToEdit}
        />
      )}
    </div>
  );
};

export default AdminEventsPage;
